import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'selectedFraccionamientoId';

// Use sessionStorage instead of localStorage for better security
// sessionStorage is cleared when the browser tab is closed
const safeStorage = {
  getItem: (key) => {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      console.warn('sessionStorage not available');
    }
  }
};

const FraccionamientoContext = createContext(null);

export function FraccionamientoProvider({ children }) {
  const [fraccionamientos, setFraccionamientos] = useState([]);
  const [selectedFraccId, setSelectedFraccId] = useState(() => {
    // Recuperar de sessionStorage al iniciar
    return safeStorage.getItem(STORAGE_KEY) || 'todos';
  });
  const [loading, setLoading] = useState(true);
  const [isChanging, setIsChanging] = useState(false); // Para mostrar loader al cambiar
  const [refreshKey, setRefreshKey] = useState(0); // Para forzar refetch en todas las páginas

  // Cargar fraccionamientos
  const fetchFraccionamientos = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/fraccionamientos`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFraccionamientos(data);
        
        // Validar que el ID guardado aún existe
        const savedId = safeStorage.getItem(STORAGE_KEY);
        if (savedId && savedId !== 'todos') {
          const exists = data.some(f => f.fraccionamiento_id === savedId);
          if (!exists) {
            setSelectedFraccId('todos');
            safeStorage.setItem(STORAGE_KEY, 'todos');
          }
        }
      }
    } catch (error) {
      console.error('Error fetching fraccionamientos:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFraccionamientos();
  }, [fetchFraccionamientos]);

  // Función para cambiar fraccionamiento con reset completo
  const changeFraccionamiento = useCallback(async (newFraccId) => {
    if (newFraccId === selectedFraccId) return;
    
    // Mostrar loader mientras se cambia
    setIsChanging(true);
    
    // Actualizar estado y sessionStorage
    setSelectedFraccId(newFraccId);
    safeStorage.setItem(STORAGE_KEY, newFraccId);
    
    // Incrementar refreshKey para forzar refetch en todas las páginas
    setRefreshKey(prev => prev + 1);
    
    // Pequeño delay para asegurar que el loader se muestre
    await new Promise(resolve => setTimeout(resolve, 100));
    
    setIsChanging(false);
  }, [selectedFraccId]);

  // Obtener nombre del fraccionamiento seleccionado
  const selectedFraccName = selectedFraccId === 'todos' 
    ? 'Todos los fraccionamientos'
    : fraccionamientos.find(f => f.fraccionamiento_id === selectedFraccId)?.nombre || '';

  // 🔥 Helper: Construir query param para API calls
  const getFilterParam = useCallback(() => {
    if (selectedFraccId === 'todos') return '';
    return `fraccionamiento_id=${selectedFraccId}`;
  }, [selectedFraccId]);

  // 🔥 Helper: Construir URL con filtro
  const buildUrl = useCallback((baseUrl, additionalParams = {}) => {
    const params = new URLSearchParams();
    
    if (selectedFraccId !== 'todos') {
      params.set('fraccionamiento_id', selectedFraccId);
    }
    
    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value);
      }
    });
    
    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }, [selectedFraccId]);

  const value = {
    fraccionamientos,
    selectedFraccId,
    selectedFraccName,
    loading,
    isChanging,
    refreshKey,
    changeFraccionamiento,
    getFilterParam,
    buildUrl,
    refreshFraccionamientos: fetchFraccionamientos
  };

  return (
    <FraccionamientoContext.Provider value={value}>
      {children}
    </FraccionamientoContext.Provider>
  );
}

export function useFraccionamiento() {
  const context = useContext(FraccionamientoContext);
  if (!context) {
    throw new Error('useFraccionamiento must be used within a FraccionamientoProvider');
  }
  return context;
}

// 🔥 Hook para usar en páginas que necesitan reaccionar al cambio de fraccionamiento
export function useFraccionamientoEffect(callback, dependencies = []) {
  const { refreshKey, selectedFraccId, isChanging } = useFraccionamiento();
  
  useEffect(() => {
    if (!isChanging) {
      callback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey, selectedFraccId, ...dependencies]);
}
